/**
 * Carrier MCP — ATLAS wallet client.
 *
 * Typed, server-only HTTP wrapper around the ATLAS prepaid-wallet service.
 * The wallet itself (balance, ledger, auto-top-up config) lives in ATLAS
 * (Hono + D1); this module is the boundary the MCP server uses to fund and
 * read those wallets. It never stores wallet state locally.
 *
 * Contract (ATLAS internal API):
 *   POST /internal/wallet/:orgId/topup       { eur_cents, ref, type? }
 *        -> { balance_after_cents }
 *   GET  /internal/wallet/:orgId
 *        -> { balance_eur_cents, auto_topup_enabled,
 *             auto_topup_threshold_cents, auto_topup_pack_cents }
 *        Both auto-top-up amounts are null until auto-top-up is configured,
 *        and ATLAS synthesises an all-null row for an org that has no wallet
 *        yet (atlas/src/internal/wallet.ts, toWalletView).
 *   PUT  /internal/wallet/:orgId/auto-topup  { enabled, threshold_cents?, pack_cents? }
 *        -> WalletState (echoes updated config)
 *
 * Auth: every request carries `X-Carrier-Internal-Key: <CARRIER_INTERNAL_API_KEY>`.
 * Base URL: env.ATLAS_BASE_URL ?? "https://atlas.carrier.llc".
 */

import type { Env } from "./types.js";

const DEFAULT_ATLAS_BASE_URL = "https://atlas.carrier.llc";

// ---------------------------------------------------------------------------
// Response shapes (only fields the contract guarantees)
// ---------------------------------------------------------------------------

export interface WalletState {
  balance_eur_cents: number;
  auto_topup_enabled: boolean;
  /** Null until auto-top-up is configured — ATLAS types both as nullable. */
  auto_topup_threshold_cents: number | null;
  auto_topup_pack_cents: number | null;
}

export interface WalletTopupResult {
  balance_after_cents: number;
}

/** Top-up provenance — drives ledger categorisation on the ATLAS side. */
export type WalletTopupType = "bulk_pack" | "auto_topup" | "adjustment";

export interface WalletAutoTopupConfig {
  enabled: boolean;
  threshold_cents?: number;
  pack_cents?: number;
}

/**
 * Raised when an ATLAS wallet call fails (non-2xx, missing config, or transport
 * error). Carries the HTTP status (0 for transport/config failures) so callers
 * can decide whether to surface or retry.
 */
export class WalletClientError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "WalletClientError";
    this.status = status;
  }
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

function resolveConfig(env: Env): { baseUrl: string; key: string } {
  const key = env.CARRIER_INTERNAL_API_KEY;
  if (!key) {
    throw new WalletClientError(
      "CARRIER_INTERNAL_API_KEY not configured — cannot reach ATLAS wallet",
      0,
    );
  }
  const baseUrl = (env.ATLAS_BASE_URL ?? DEFAULT_ATLAS_BASE_URL).replace(
    /\/+$/,
    "",
  );
  return { baseUrl, key };
}

async function atlasFetch<T>(
  env: Env,
  path: string,
  init: { method: string; body?: unknown },
): Promise<T> {
  const { baseUrl, key } = resolveConfig(env);
  const headers: Record<string, string> = { "X-Carrier-Internal-Key": key };
  let body: string | undefined;
  if (init.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(init.body);
  }

  let res: Response;
  try {
    res = await fetch(`${baseUrl}${path}`, { method: init.method, headers, body });
  } catch (e) {
    throw new WalletClientError(
      `ATLAS wallet request failed: ${e instanceof Error ? e.message : "network error"}`,
      0,
    );
  }

  if (!res.ok) {
    const detail = await res.text().catch(() => "");
    throw new WalletClientError(
      `ATLAS wallet ${init.method} ${path} -> HTTP ${res.status}${detail ? `: ${detail}` : ""}`,
      res.status,
    );
  }

  return (await res.json()) as T;
}

/**
 * Credit a wallet. `eurCents` is the total credited amount (pack + bonus).
 * `ref` should be the originating Stripe id (event/PI/session) for traceability
 * and idempotency on the ATLAS ledger.
 */
export async function walletTopup(
  env: Env,
  orgId: string,
  args: { eurCents: number; ref: string; type?: WalletTopupType },
): Promise<WalletTopupResult> {
  return atlasFetch<WalletTopupResult>(
    env,
    `/internal/wallet/${encodeURIComponent(orgId)}/topup`,
    {
      method: "POST",
      body: {
        eur_cents: args.eurCents,
        ref: args.ref,
        ...(args.type ? { type: args.type } : {}),
      },
    },
  );
}

/** Read a wallet's balance + auto-top-up configuration. */
export async function getWallet(env: Env, orgId: string): Promise<WalletState> {
  return atlasFetch<WalletState>(env, `/internal/wallet/${encodeURIComponent(orgId)}`, {
    method: "GET",
  });
}

/** Update a wallet's auto-top-up configuration. */
export async function setAutoTopup(
  env: Env,
  orgId: string,
  config: WalletAutoTopupConfig,
): Promise<WalletState> {
  return atlasFetch<WalletState>(
    env,
    `/internal/wallet/${encodeURIComponent(orgId)}/auto-topup`,
    {
      method: "PUT",
      body: {
        enabled: config.enabled,
        ...(config.threshold_cents !== undefined
          ? { threshold_cents: config.threshold_cents }
          : {}),
        ...(config.pack_cents !== undefined ? { pack_cents: config.pack_cents } : {}),
      },
    },
  );
}
